'use client'

import { Calendar, Settings } from 'lucide-react'

import { useEffect, useState } from 'react'

import { LifeInWeeks } from '@/components/molecules/LifeInWeeks'
import { useUserMetadataContext } from '@/contexts/UserMetadataContext'
import { cn } from '@/lib/utils'

export default function MyLifeInWeeksPage() {
  const [birthDate, setBirthDate] = useState<Date>(() => {
    // Default birth date fallback
    return new Date(1990, 0, 1) // January 1, 1990
  })
  const [expectedLifespan, setExpectedLifespan] = useState(80)
  const [showSettings, setShowSettings] = useState(false)

  const { userMetadata, loading } = useUserMetadataContext()

  useEffect(() => {
    if (userMetadata && userMetadata.birthDate && !loading) {
      setBirthDate(new Date(userMetadata.birthDate))
    }
  }, [userMetadata, loading])

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value)
    if (!isNaN(date.getTime())) {
      setBirthDate(date)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {userMetadata?.displayName || 'My'} Life in Weeks
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            This visualization shows your entire life as a grid of weeks. Each
            small box represents one week. The filled boxes are weeks you've
            already lived, and the empty ones are what you (hopefully) have
            left.
          </p>
          {userMetadata?.displayName && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Welcome back, {userMetadata.displayName}!
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
              'bg-blue-600 hover:bg-blue-700 text-white',
              'focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              'dark:focus:ring-offset-gray-900'
            )}
          >
            <Settings size={16} />
            Settings
          </button>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="max-w-md mx-auto mb-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Configuration
            </h3>

            {loading && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Loading your profile...
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar size={16} className="inline mr-2" />
                  Birth Date
                  {userMetadata?.birthDate && (
                    <span className="text-xs text-green-600 dark:text-green-400 ml-2">
                      (from your profile)
                    </span>
                  )}
                </label>
                <input
                  type="date"
                  value={birthDate.toISOString().split('T')[0]}
                  onChange={handleBirthDateChange}
                  disabled={loading}
                  className={cn(
                    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600',
                    'rounded-lg shadow-xs bg-white dark:bg-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                />
                {!userMetadata?.birthDate && !loading && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    No birth date in your profile. Using default date.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Expected Lifespan (years)
                </label>
                <input
                  type="number"
                  min="60"
                  max="120"
                  value={expectedLifespan}
                  onChange={e => setExpectedLifespan(Number(e.target.value))}
                  className={cn(
                    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600',
                    'rounded-lg shadow-xs bg-white dark:bg-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  )}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Global average is around 72-80 years
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Life in Weeks Visualization */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <LifeInWeeks
            birthDate={birthDate}
            expectedLifespan={expectedLifespan}
          />
        </div>

        {/* Insights */}
        <div className="mt-8 max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                What This Shows
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <li>• Each box represents one week of your life</li>
                <li>• Filled boxes are weeks you've already lived</li>
                <li>• Empty boxes are your remaining weeks (hopefully)</li>
                <li>• The blue box is your current week</li>
                <li>• Each row represents one year (52 weeks)</li>
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Why This Matters
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <li>• Puts your life into perspective</li>
                <li>• Makes time feel tangible and finite</li>
                <li>• Encourages mindful living</li>
                <li>• Helps prioritize what's important</li>
                <li>• Motivates action on long-term goals</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
              Remember
            </h3>
            <p className="text-blue-800 dark:text-blue-200 text-sm leading-relaxed">
              This is just an approximation. Quality matters more than quantity.
              Each week is an opportunity to create meaning, build
              relationships, learn something new, or make a positive impact. The
              goal isn't to rush through life, but to live it intentionally.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
